import { Toaster } from "@/components/ui/sonner";
import { Toaster as ToasterShadcn } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "@/store";
import { Layout } from "@/components/layout/Layout";
import { BrandingProvider } from "@/components/layout/BrandingProvider";
import { ProductsProvider } from "@/components/products/ProductsContext";
import { LoginForm } from "@/components/auth/LoginForm";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SuperAdminRoute } from "@/components/auth/SuperAdminRoute";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { useAuthInit } from "@/hooks/useAuthInit";
import Dashboard from "./pages/Dashboard";
import Companies from "./pages/Companies";
import Contacts from "./pages/Contacts";
import Clients from "./pages/Clients";
import Opportunities from "./pages/Opportunities";
import Approvals from "./pages/Approvals";
import Suppliers from "./pages/Suppliers";

import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Products from "./pages/Products";
import { ForgotPassword } from "./pages/ForgotPassword";
import { ResetPassword } from "./pages/ResetPassword";
import Users from "./pages/Users";
import CompaniesAdmin from "./pages/CompaniesAdmin";
import WelcomePage from "./pages/Index";
import { PublicPlansPage } from "./pages/PublicPlansPage";
import { PlanRegistrationPage } from "./pages/PlanRegistrationPage";
import Reports from "./pages/Reports";
import PipelinesAdmin from "./pages/PipelinesAdmin";
import Employees from "./pages/Employees";
import Attendance from "@/pages/Attendance";
import Inventory from "./pages/Inventory";
import MachineryPage from "./pages/Machinery";
import OrganizationSettings from "./pages/OrganizationSettings";

import { ErrorBoundary } from "@/components/utils/ErrorBoundary";
import Unauthorized from "./pages/Unauthorized";

const queryClient = new QueryClient();

const AuthInitializer = ({ children }: { children: React.ReactNode }) => {
  useAuthInit();
  return <BrandingProvider>{children}</BrandingProvider>;
};

const App = () => (
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <ToasterShadcn />
          <HashRouter>
            <AuthInitializer>
              <ProductsProvider>
                <ErrorBoundary>
                  <Routes>
                  {/* Rutas PÚBLICAS */}
                  <Route path="/" element={<Navigate to="/login" replace />} />
                  <Route path="/planes" element={<PublicPlansPage />} />
                  <Route path="/precios" element={<PublicPlansPage />} />
                  <Route path="/pricing" element={<PublicPlansPage />} />

                  <Route
                    path="/register/:planId?"
                    element={<PlanRegistrationPage />}
                  />
                  <Route
                    path="/registro/:planId?"
                    element={<PlanRegistrationPage />}
                  />

                  {/* Rutas de autenticación */}
                  <Route path="/login" element={<LoginForm />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />

                  {/* Rutas PROTEGIDAS */}
                  <Route
                    path="/index"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <WelcomePage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Reports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contacts"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Contacts />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/clients"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Clients />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/companies"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Companies />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/opportunities"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Opportunities />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/approvals"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <Approvals />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/suppliers"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Suppliers />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/products"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Products />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Employees />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/attendance"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Attendance />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inventory"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Inventory />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/machinery"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <MachineryPage />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Settings />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/organization/settings"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <OrganizationSettings />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Users />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <Reports />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pipelinesadmin"
                    element={
                      <ProtectedRoute>
                        <Layout>
                          <PipelinesAdmin />
                        </Layout>
                      </ProtectedRoute>
                    }
                  />

                  {/* Rutas para ADMIN */}
                  <Route
                    path="/companies"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <Companies />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pipelinesadmin"
                    element={
                      <ProtectedRoute>
                        <AdminRoute>
                          <Layout>
                            <PipelinesAdmin />
                          </Layout>
                        </AdminRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Rutas para SUPER ADMIN */}

                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute>
                        <SuperAdminRoute>
                          <Layout>
                            <Users />
                          </Layout>
                        </SuperAdminRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/companies"
                    element={
                      <ProtectedRoute>
                        <SuperAdminRoute>
                          <Layout>
                            <CompaniesAdmin />
                          </Layout>
                        </SuperAdminRoute>
                      </ProtectedRoute>
                    }
                  />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </ErrorBoundary>
            </ProductsProvider>
          </AuthInitializer>
        </HashRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </PersistGate>
</Provider>
);

export default App;
